import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Loader2,
  Download,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  Search,
  Calendar,
  CreditCard,
  Banknote,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const PaymentsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({
    period: "month",
    status: "",
    type: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchSummary();
    fetchTransactions();
  }, [filters.period]);

  useEffect(() => {
    fetchTransactions();
  }, [pagination.page, filters.status, filters.type]);

  const fetchSummary = async () => {
    try {
      const response = await api.get(
        `/payments/summary?period=${filters.period}`,
      );
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let url = `/payments/history?page=${pagination.page}&limit=20&period=${filters.period}`;
      if (filters.status) url += `&status=${filters.status}`;
      if (filters.type) url += `&payment_type=${filters.type}`;

      const response = await api.get(url);
      setTransactions(response.data.transactions);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total,
      });
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      if (format === "csv") {
        const response = await api.get("/payments/export?format=csv", {
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `payments_${new Date().toISOString().split("T")[0]}.csv`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Payment history exported");
      } else {
        const response = await api.get("/payments/export?format=json");
        const dataStr = JSON.stringify(response.data, null, 2);
        const url = window.URL.createObjectURL(
          new Blob([dataStr], { type: "application/json" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `payments_${new Date().toISOString().split("T")[0]}.json`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Payment history exported");
      }
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export payment history");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    try {
      const [h, m] = timeStr.split(":");
      const hour = parseInt(h);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${m} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      failed: "bg-red-100 text-red-700",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-700"}`}
      >
        {status}
      </span>
    );
  };

  const getTypeBadge = (type, manual) => {
    if (manual) {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 flex items-center gap-1">
          <Banknote className="w-3 h-3" />
          Manual
        </span>
      );
    }
    const styles = {
      deposit: "bg-purple-100 text-purple-700",
      balance: "bg-blue-100 text-blue-700",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type] || "bg-gray-100 text-gray-700"} flex items-center gap-1`}
      >
        <CreditCard className="w-3 h-3" />
        {type === "deposit" ? "Deposit" : "Balance"}
      </span>
    );
  };

  const filteredTransactions = transactions.filter((t) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      t.customer_name?.toLowerCase().includes(search) ||
      t.customer_email?.toLowerCase().includes(search) ||
      t.service_name?.toLowerCase().includes(search)
    );
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="payments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold">Payments</h1>
            <p className="text-muted-foreground">
              Track deposits, balances, and revenue
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleExport("csv")}
              disabled={exporting}
              data-testid="export-csv-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
          </div>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2">
          {["today", "week", "month", "year", "all"].map((period) => (
            <Button
              key={period}
              variant={filters.period === period ? "default" : "outline"}
              size="sm"
              onClick={() => setFilters((f) => ({ ...f, period }))}
              className="capitalize"
            >
              {period === "all" ? "All Time" : period}
            </Button>
          ))}
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card-base" data-testid="total-collected-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Collected
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(summary.total_collected)}
                  </p>
                </div>
              </div>
            </div>

            <div className="card-base" data-testid="deposits-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Deposits</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(summary.deposits?.total)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.deposits?.count} transactions
                  </p>
                </div>
              </div>
            </div>

            <div className="card-base" data-testid="balances-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Balance Payments
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(summary.balances?.total)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.balances?.count} transactions
                  </p>
                </div>
              </div>
            </div>

            <div className="card-base" data-testid="pending-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Pending Balances
                  </p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {formatCurrency(summary.pending?.total)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.pending?.count} appointments
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="card-base">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by customer or service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              value={filters.type || "all"}
              onValueChange={(value) =>
                setFilters((f) => ({
                  ...f,
                  type: value === "all" ? "" : value,
                }))
              }
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="deposit">Deposits</SelectItem>
                <SelectItem value="balance">Balances</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) =>
                setFilters((f) => ({
                  ...f,
                  status: value === "all" ? "" : value,
                }))
              }
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Transactions Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No transactions found</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                        Payment Date
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                        Customer
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                        Service
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                        Appointment
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                        Type
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                        Status
                      </th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((t) => (
                      <tr
                        key={t.id}
                        className="border-b hover:bg-secondary/30 transition-colors"
                      >
                        <td className="py-3 px-4 text-sm">
                          {formatDate(t.created_at)}
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">
                              {t.customer_name || "-"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {t.customer_email}
                            </p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {t.service_name || "-"}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {t.appointment_date ? (
                            <div>
                              <p>
                                {new Date(
                                  t.appointment_date + "T00:00:00",
                                ).toLocaleDateString("en-AU", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </p>
                              {t.appointment_time && (
                                <p className="text-xs text-muted-foreground">
                                  {formatTime(t.appointment_time)}
                                </p>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {getTypeBadge(t.type, t.manual)}
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(t.status)}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">
                          {formatCurrency(t.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filteredTransactions.map((t) => (
                  <div key={t.id} className="p-4 border rounded-xl">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium">
                          {t.customer_name || "Unknown"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t.service_name}
                        </p>
                      </div>
                      <p className="font-bold text-primary">
                        {formatCurrency(t.amount)}
                      </p>
                    </div>
                    {t.appointment_date && (
                      <p className="text-xs text-muted-foreground mb-1.5">
                        Appt:{" "}
                        {new Date(
                          t.appointment_date + "T00:00:00",
                        ).toLocaleDateString("en-AU", {
                          day: "numeric",
                          month: "short",
                        })}
                        {t.appointment_time &&
                          ` at ${formatTime(t.appointment_time)}`}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        {getTypeBadge(t.type, t.manual)}
                        {getStatusBadge(t.status)}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(t.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {(pagination.page - 1) * 20 + 1} to{" "}
                    {Math.min(pagination.page * 20, pagination.total)} of{" "}
                    {pagination.total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() =>
                        setPagination((p) => ({ ...p, page: p.page - 1 }))
                      }
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() =>
                        setPagination((p) => ({ ...p, page: p.page + 1 }))
                      }
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PaymentsPage;
